/**
 * 크레딧 상세 페이지 — 잔액 / 이번 달 사용 요약 / 최근 트랜잭션 / 충전 패키지
 *
 *  진입점:
 *    1) 헤더 우측 CreditBadge 칩 클릭
 *    2) 마이페이지의 "크레딧" 카드 클릭
 *
 *  Phase 1 — mock 데이터로 화면 시각만 우선. BE 연동은 같은 훅 시그니처로 swap.
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { LayoutHeader } from "@/Header";
import { PaymentHistoryContent } from "@/business/credit/PaymentHistoryContent";
import { openWithHandoffIfApp } from "%/handoff";
import {
  useCompanyCreditWallet,
  useCompanyCreditTxList,
  type CreditTxItem,
} from "~/services/business/credit";
import { CREDIT, CREDIT_TX_LABEL, getCreditLevel } from "%/credit";

export const Route = createFileRoute("/business/layout/user-credit-wallet")({
  component: CreditWallet,
});

/* ─────────────────────────────────────────────────────────────────
 *  날짜·금액 포맷 유틸
 * ───────────────────────────────────────────────────────────────── */
const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const today = new Date();
  const isToday = d.toDateString() === today.toDateString();
  if (isToday) {
    return `오늘 ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  }
  return `${d.getMonth() + 1}.${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/* ─────────────────────────────────────────────────────────────────
 *  메인 페이지
 * ───────────────────────────────────────────────────────────────── */
type WalletTab = "recent" | "payments";

function CreditWallet() {
  const navigate = useNavigate();
  const { data: wallet } = useCompanyCreditWallet();
  // 사용내역 탭 — 차감 + 이용권 회수 환원을 서버 필터로 조회 (페이지네이션 정합)
  //   환원은 CREDIT 방향이라 DEBIT 필터로는 빠지고, 충전내역 탭은 PG 결제 목록이라
  //   거기에도 안 나온다. USAGE 필터로 되돌아온 크레딧까지 함께 보여준다.
  const { data: txData } = useCompanyCreditTxList({ page: 0, size: 20, direction: "USAGE" });

  const txItems = txData?.content ?? [];
  const [tab, setTab] = useState<WalletTab>("recent");

  // 충전(결제)은 PG 흐름 — 앱이면 외부 브라우저(handoff), 웹이면 SPA 이동
  const goCharge = () =>
    openWithHandoffIfApp(
      "/business/layout/user-credit-wallet-charge",
      () => navigate({ to: "/business/layout/user-credit-wallet-charge" }),
    );

  // 크레딧 이전은 항상 설정 화면.
  //  · 카드 탭 클릭 → 크레딧 진입은 단일 경로이고, 충전→토스→성공→크레딧 흐름의
  //    중간 히스토리를 노출하지 않도록 뒤로가기를 명시적으로 설정으로 리다이렉트.
  const goSettings = () =>
    navigate({ to: "/business/layout/user-owner-setting", replace: true });

  // 브라우저/OS 뒤로가기 제스처도 동일 동작 — popstate 한 번 intercept 후 설정으로
  useEffect(() => {
    // 현재 엔트리에 marker 를 추가해 뒤로가기 시점 감지
    window.history.replaceState({ ...(window.history.state ?? {}), _walletGuard: true }, "");
    const onPop = () => {
      // 사용자가 OS/브라우저 back 으로 빠져나가려 함 → 설정으로 강제 이동
      navigate({ to: "/business/layout/user-owner-setting", replace: true });
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [navigate]);

  return (
    <>
      <LayoutHeader title="크레딧" hasBackBtn hasFootPrint={false} onBackClick={goSettings} />
      <div className="flex-1 min-h-0 flex flex-col bg-[#F5F5F5]">
        {/* ─── 잔액 카드 — 항상 최상단 고정 (+ 충전하기) ─── */}
        <div className="shrink-0 pb-2">
          <BalanceCard wallet={wallet} onCharge={goCharge} />
        </div>

        {/* ─── 탭 바 — 사용내역 / 충전내역 ─── */}
        <div className="flex bg-white border-b border-[#E9E9E9] shrink-0">
          <TabButton
            active={tab === "recent"}
            label="사용내역"
            onClick={() => setTab("recent")}
          />
          <TabButton
            active={tab === "payments"}
            label="충전내역"
            onClick={() => setTab("payments")}
          />
        </div>

        {/* ─── 탭 콘텐츠 — 스크롤 ─── */}
        <div className="flex-1 min-h-0 overflow-y-auto pt-2">
          {tab === "recent" && <TxListCard items={txItems} />}
          {tab === "payments" && <PaymentHistoryContent />}
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────────
 *  탭 버튼
 * ───────────────────────────────────────────────────────────────── */
const TabButton = ({
  active, label, onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 py-3 text-[14px] font-semibold transition-colors ${
      active
        ? "text-[#FF9934] border-b-2 border-[#FF9934]"
        : "text-[#999] border-b-2 border-transparent"
    }`}
  >
    {label}
  </button>
);

/* ─────────────────────────────────────────────────────────────────
 *  잔액 카드 — 상단 히어로
 * ───────────────────────────────────────────────────────────────── */
const BalanceCard = ({
  wallet,
  onCharge,
}: {
  wallet: ReturnType<typeof useCompanyCreditWallet>["data"];
  onCharge: () => void;
}) => {
  const balance = wallet?.balance ?? 0;
  const free = wallet?.freeBalance ?? 0;
  const paid = wallet?.paidBalance ?? 0;
  const level = getCreditLevel(balance);

  return (
    <div className="mx-3 mt-4">
      <div
        className="rounded-2xl border border-[#FFE3B3] p-5"
        style={{ background: "linear-gradient(135deg, #FFF7E6 0%, #FFFFFF 100%)" }}
      >
        <div className="flex items-baseline gap-2 mb-3">
          <span className="text-[28px]">{CREDIT.emoji}</span>
          <span className={`text-[36px] font-extrabold leading-none ${
            level === "low" ? "text-[#DC2626]" :
            level === "warn" ? "text-[#D97706]" :
            "text-[#FF9934]"
          }`}>
            {balance.toLocaleString()}
          </span>
          <span className="text-[14px] text-[#666] font-medium">{CREDIT.unit}</span>
        </div>

        {/* 구매 / 무료 분리 — 상하 정렬 행 */}
        {balance > 0 && (
          <div className="border-t border-[#FFE3B3] pt-3 space-y-1.5">
            <div className="flex justify-between text-[13px]">
              <span className="text-[#888]">구매 {CREDIT.unit}</span>
              <span className="font-semibold text-[#333] tabular-nums">{paid.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-[13px]">
              <span className="text-[#888]">무료 {CREDIT.unit}</span>
              <span className="font-semibold text-[#333] tabular-nums">{free.toLocaleString()}</span>
            </div>
            {wallet?.dtFreeExpire && free > 0 && (
              <div className="text-[10px] text-[#999] pt-0.5">
                ⏳ 무료분 만료: {new Date(wallet.dtFreeExpire).toLocaleDateString("ko-KR")}
              </div>
            )}
          </div>
        )}

        {/* 부족 시 안내 배너 */}
        {level === "low" && (
          <div className="mt-3 p-2.5 rounded-lg bg-[#FEF2F2] text-[12px] text-[#DC2626] font-medium">
            ⚠ {CREDIT.unit}이 부족해요. 멍투데이 결제로 발급하면 차감 없이 사용 가능해요!
          </div>
        )}

        {/* 충전하기 — 충전(웹) 페이지로 이동 */}
        <button
          type="button"
          onClick={onCharge}
          className="mt-4 w-full py-3 rounded-xl bg-[#FF9934] text-white text-[15px] font-bold transition-transform active:scale-[0.98]"
        >
          {CREDIT.actions.charge}하기
        </button>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
 *  사용내역 — 시간 역순 (DEBIT)
 * ───────────────────────────────────────────────────────────────── */
const TxListCard = ({ items }: { items: CreditTxItem[] }) => {
  if (!items.length) {
    return (
      <div className="mx-3 mt-2 bg-white rounded-2xl border border-[#E9E9E9] p-6 text-center text-[13px] text-[#999]">
        아직 사용 내역이 없어요
      </div>
    );
  }

  return (
    <div className="mx-3 mt-2 bg-white rounded-2xl border border-[#E9E9E9] overflow-hidden">
      {items.map((tx, idx) => (
        <TxRow key={tx.txSeq} tx={tx} isLast={idx === items.length - 1} />
      ))}
    </div>
  );
};

const TxRow = ({ tx, isLast }: { tx: CreditTxItem; isLast: boolean }) => {
  const meta = CREDIT_TX_LABEL[tx.cdTxType] ?? { label: tx.cdTxType, emoji: "📌" };
  const isCredit = tx.cdDirection === "CREDIT";
  return (
    <div className={`flex items-center gap-3 px-4 py-3 ${isLast ? "" : "border-b border-[#F0F0F0]"}`}>
      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#FAFAFA] text-[16px] shrink-0">
        {meta.emoji}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-[#111] font-medium truncate">
          {tx.description}
        </div>
        <div className="text-[10px] text-[#999] mt-0.5">
          {meta.label} · {formatDate(tx.dtRegist)}
        </div>
      </div>
      <div
        className={`text-[14px] font-bold tabular-nums ${
          isCredit ? "text-[#22A55B]" : "text-[#111]"
        }`}
      >
        {isCredit ? "+" : "-"}
        {tx.amount.toLocaleString()}
      </div>
    </div>
  );
};

